"use client"

import type { ReactNode } from "react"
import { Link } from "react-router-dom"
import { ChevronRightIcon } from "lucide-react"

import { Skeleton, cn } from "@ibada/ui"

export type HomeTileLink = { label: string; href: string }

/**
 * One D4 tile: title, optional subtitle, an optional link at the right, and a body that fills the rest. The tile
 * takes the full height of its grid cell so every tile in a row is the same height.
 */
export function HomeTile({
  title,
  subtitle,
  link,
  children,
  className,
  testId,
}: {
  title: string
  subtitle?: string | null
  link?: HomeTileLink | null
  children: ReactNode
  className?: string
  testId?: string
}) {
  return (
    <section
      data-testid={testId}
      className={cn(
        "flex h-full min-h-0 flex-col gap-3 overflow-hidden rounded-2xl border border-border/60 bg-card px-[18px] py-4",
        className,
      )}
    >
      <header className='flex items-start gap-2'>
        <span className='flex min-w-0 flex-col gap-px'>
          <h2 className='truncate text-sm font-bold text-foreground'>{title}</h2>
          {subtitle ? <span className='truncate text-xs text-muted-foreground'>{subtitle}</span> : null}
        </span>
        {link ? (
          <Link
            to={link.href}
            className='ml-auto inline-flex shrink-0 items-center gap-0.5 rounded-md text-[12.5px] font-bold whitespace-nowrap text-primary hover:underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none'
          >
            {link.label}
            <ChevronRightIcon aria-hidden className='size-3.5' />
          </Link>
        ) : null}
      </header>
      <div className='flex min-h-0 flex-1 flex-col gap-2'>{children}</div>
    </section>
  )
}

/** A tile while its data loads: the same frame, with placeholder lines where the content will be. */
export function HomeTileSkeleton({ hero = false, testId }: { hero?: boolean; testId?: string }) {
  return (
    <section
      aria-busy
      data-testid={testId}
      className='flex h-full min-h-0 flex-col gap-3 overflow-hidden rounded-2xl border border-border/60 bg-card px-[18px] py-4'
    >
      <div className='flex flex-col gap-1.5'>
        <Skeleton className='h-3.5 w-28' />
        <Skeleton className='h-3 w-40' />
      </div>
      {hero ? (
        <div className='flex flex-1 gap-7'>
          <div className='flex w-60 flex-col gap-3'>
            <Skeleton className='h-10 w-52 rounded-lg' />
            <Skeleton className='h-5 w-28 rounded-full' />
            <Skeleton className='mt-3 h-3.5 w-full' />
            <Skeleton className='h-3.5 w-full' />
            <Skeleton className='h-3.5 w-full' />
          </div>
          <Skeleton className='h-full flex-1 rounded-xl' />
        </div>
      ) : (
        <div className='flex flex-1 flex-col gap-3'>
          <Skeleton className='h-7 w-24 rounded-lg' />
          <Skeleton className='h-3 w-full' />
          <Skeleton className='h-3 w-5/6' />
          <Skeleton className='h-3 w-2/3' />
        </div>
      )}
    </section>
  )
}

/** A short helpful empty state inside a tile, with an optional action. */
export function HomeTileEmpty({ message, action }: { message: string; action?: HomeTileLink | null }) {
  return (
    <div className='flex flex-1 flex-col items-start justify-center gap-2'>
      <p className='text-[13px] text-muted-foreground'>{message}</p>
      {action ? (
        <Link
          to={action.href}
          className='inline-flex items-center rounded-lg border border-border px-2.5 py-1.5 text-[12.5px] font-bold text-foreground hover:bg-muted'
        >
          {action.label}
        </Link>
      ) : null}
    </div>
  )
}
